import { ColumnState } from "ag-grid-community";
import { AgGridSortItem } from '../ag-grid-helper.models';
import ModuleDataGroupItem from '../module-data-group-item';
import { ModuleManagerExportFormat } from "../module-manager/module-manager-options.model";

export interface ReportingSetData {
    FieldDefinitions: ReportingSetFieldDefinition[];
    DataRows: ReportingSetDataRow[];
}

export enum ReportingSetFieldType {
    String,
    Int,
    Double,
    DateTime,
    Boolean,
    Null,
    Time
}

export enum ReportingSetType {
    Standard = 0,
    RealTime = 1,
}

export enum DataSetUpdateMode {
    Standard = 0,
    DropAllOnImport = 1,
}

export interface ReportingSetFieldDefinition {
    FieldName: string;
    FieldDisplay: string;
    FieldType: ReportingSetFieldType;
    Groupable: boolean;
}

export interface ReportingSetDataRow {
    Group: ModuleDataGroupItem;

    /** The rest of the properties are dynamic from a Dictionary<string, object> */
    [fieldName: string]: any;
}

export interface DataSetStatus {
    IsValid: boolean;
    ConsecutiveFailureCount: number;
    LastFailureDate?: string|Date;
    LastFailureReportID?: string;
    LastSuccessDate?: string|Date;
    LastSuccessReportID?: string;
}

export interface DataFeedJoinKey {
    EvalFields: string[];
    MapsToFields: string[];
    MapsToCompanyID: number;
    MapsToJoinID: number;
}

export interface DataFeedJoin {
    JoinID: number;
    JoinName: string;
    FeedName: string;
    SourceSystem: string;
    JoinKey: DataFeedJoinKey;
    Required: boolean;
    ReverseJoinsEnabled: boolean;
}

export interface DataFieldDefinition {
    ExpectedType: ReportingSetFieldType;
    JoinID: number;
    Field: string;
    Groupable?: boolean;
    DefaultGroupable?: boolean;
    DisplayName?: string;
    ColumnField?: string;
}

/** Represents a Reporting Set Definition api model (wrapping a Reporting Set Definition). 
 *  This object is serialized from "ReportingSetDefinitionApiModel" defined in 
 *  WebPortal/AppCode/Api/Controllers/Pipeline/PipelineDataController.cs. Please keep these in sync */
export interface ReportingSetDefinitionApiModel {
    ReportingSetDefinition: ReportingSetDefinition;

    ReportingSetNameForReports: string;
    SupportsGrouping: boolean;
    AgGridGroupable: boolean;
}

export interface ReportingSetDefinition extends AbstractDataSetDefinition {
    _id: string;
    ReportingSetName: string;
    DisplayName: string;
    ForReports: boolean;
    MaxReportDays?: number;
    CanExport: boolean;
    ReportFilterableFields: { [FieldName: string]: FilterableField };
    ReportSelectorSettings: ReportSelectorSettings;
    ReportingSetGroup: string;
    ReportingSetDescription: string;
    Mode: DataSetUpdateMode;
    QueuedOnImport: boolean;
    Type: ReportingSetType;
    AgGridGroupable: boolean;

    Dates: { [FieldName: string]: DataFieldDefinition };
    EmpHierarchy: { [FieldName: string]: DataFieldDefinition };
    ContactHierarchy: { [FieldName: string]: DataFieldDefinition };
    Attributes: { [FieldName: string]: DataFieldDefinition };
    DataFields: { [FieldName: string]: DataFieldDefinition };

    ExcludeIf: DataFieldDefinition;
    IsApiSourced: boolean;
    customTmpl?: boolean; // Used by sol component to render custom template
}

export interface AbstractDataSetDefinition {
    _id: string;
    Status: DataSetStatus;
    SpecialFunction: string;
    IsEditable: boolean;
    IsViewable: boolean;
    IsTemplate: boolean;
    CompanyID: number;
    BaseFeed: string;
    BaseSourceSystem: string;
    LastModified: Date;
    TimeToLive: number;
    SKDefinitionID: string;
    Joins: DataFeedJoin[];
    ExtraElements: any[]; // BsonExtraElementsDictionary[], which can basically be any type. We'll just use any here.
}

export interface FilterableField {
    CanViewBy: boolean;
    DisplayName: string;
    ValueSource: FilterableFieldSource;
    ValueField?: string;
    ValueDisplayField?: string;
    ValueSourceReportingSet?: string;
    StaticValues?: FilterableFieldStaticValue[];
    MinRequiredSelections: number;
    MaxAllowedSelections: number;
    Dependencies?: string[];
    ExtraElements?: any; // BsonDocument, which can basically be any type. We'll just use any here.
    DynamicValues?: FilterableFieldDynamicValue[];
}

export enum FilterableFieldSource {
    Static,
    ReportingSetSourced
}

export interface FilterableFieldStaticValue {
    Value: any; // BsonValue, which can basically be any type. We'll just use any here.
    DisplayValue: string;
    selected: boolean;
}

export interface FilterableFieldDynamicValue {
    DisplayValue: string;
    selected: boolean;
    Value: string;
}

export interface ReportSelectorSettings {
    HasEmpSelector: boolean;
    EmpSelectorLevels: number[];
    HasRecSelector: boolean;
    RecSelectorLevels: number[];
    HasDateRangeSelector: boolean;
    ExtraElements: any; //this is a Bson object in the back end may need to tweak if needed later
}

export interface ReportingSetDataExportOptions {
    pageName: string;
    moduleName: string;
    columnStates?: ColumnState[];
    sortModel?: AgGridSortItem[];
    pivotMode?: boolean;
    includeHierarchy: boolean;
    includeDates: boolean;
    groupLevel: number;
    groupId: number;
    format: ModuleManagerExportFormat;

    // from ReportingSetDataFilter (only fields we need for now)
    ReportingSetName: string;
    DateField: string;
}
